import { Alert, Button, Text } from '@ksyos/design-system';
import { useState } from 'react';
import type { IPatient } from '../../../common/interfaces/patient';
import type { IRequestGetOne } from '../interfaces/requestGetOne';
import { ServiceCode } from '../../../common/enums/serviceCode';
import { ToastType } from '../../../common/enums/toastType';
import { getDisplayName } from '../../../common/utils/getDisplayName';
import { patientEmailOnlySchema } from '../../../common/validation/patient';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import { showServiceToast } from '../../utils/showToast';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../utils/validation';
import { InputText } from './common';
import { FormLabel } from './forms/FormLabel';
import { HorizontalFormGroup } from './forms/HorizontalFormGroup';
import { InputContainer } from './forms/InputContainer';
import { Modal } from './modals/Modal';

interface IResendInviteModalProps {
    onClose: () => Promise<void>;
    patient: IPatient;
    request: IRequestGetOne;
}

export function ResendInviteModal(props: IResendInviteModalProps) {
    const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);
    const [emailAddress, setEmailAddress] = useState(props.patient.emailAddress);
    const [emailUnchanged, setEmailUnchanged] = useState(false);

    return (
        <Modal
            actionButtons={[
                <Button
                    onClick={props.onClose}
                    variant="outline"
                >
                    {' '}
                    {translateKey('common.cancel')}
                </Button>,
                <Button
                    onClick={async () => {
                        await onSave();
                    }}
                >
                    {' '}
                    {translateKey('request.saveSend')}{' '}
                </Button>,
            ]}
            automation="editEmailModal"
            isOpen
            onClose={props.onClose}
            size="large"
            title="Verstuur naar ander e-mailadres"
        >
            <Text>
                Heeft je patiënt de uitnodiging niet ontvangen? Controleer het e-mailadres en verstuur de uitnodiging
                voor het onderzoek nog een keer.
            </Text>

            {emailUnchanged && (
                <Alert appearance="warning">
                    Je hebt het e-mailadres niet aangepast. Pas het eerst aan voordat je opnieuw verstuurt.
                </Alert>
            )}

            <HorizontalFormGroup hasErrors={validationErrors.has('emailAddress')}>
                <FormLabel isRequired>E-mailadres</FormLabel>
                <InputContainer>
                    <InputText
                        error={validationErrors.get('emailAddress')}
                        onChange={async (value) => setEmailAddress(value)}
                        onDoValidate={async () => validateOne()}
                        placeholder="E-mailadres"
                        value={emailAddress || ''}
                    />
                </InputContainer>
            </HorizontalFormGroup>
        </Modal>
    );

    function validateOne() {
        setValidationErrors(
            updateValidationErrorsForOne(patientEmailOnlySchema, { emailAddress }, validationErrors, 'emailAddress')
        );
    }

    function showSuccessToast() {
        const toastMessage = `Het onderzoek is verstuurd naar ${getDisplayName(props.patient)}`;
        showServiceToast(ToastType.success, undefined, undefined, 'request', toastMessage);
    }

    async function onSave() {
        const { hasErrors, errors } = validate(patientEmailOnlySchema, { emailAddress });
        setValidationErrors(errors);

        if (!hasErrors) {
            if (props.request.service.code === ServiceCode.GGZOembl) {
                if (props.request.customControlData.emailadres === emailAddress) {
                    setEmailUnchanged(true);
                } else if (props.request.customControlData.emailadres !== emailAddress) {
                    setEmailUnchanged(false);
                    await resendInvite();
                }
            } else {
                await resendInvite();
            }
        }
    }

    async function resendInvite() {
        await fetchJson(`/api/requests/${props.request.id}/resendInvite`, 'POST', { email: emailAddress });
        showSuccessToast();
        await props.onClose();
    }
}
